import { useStorage } from '@dni-hooks/useStorage'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { ErrorOutlined, Search } from '@pets-ui/icons'
import { Input, type InputProps } from '@pets-ui/Input/Input'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { STORAGE_EVENT } from '@utils'
import { type FormEventHandler, useImperativeHandle, useRef } from 'react'

const styles = stylex.create({
  input: {
    borderRadius: 4,
    width: 160,
  },
  fullWidth: {
    width: '100%',
  },
})

export interface ProductSearchInputProps extends Omit<InputProps, 'onSubmit'> {
  onSubmit?: (value: string) => void
  onClearInputValue?: () => void
}

export const ProductSearchInput = (props: ProductSearchInputProps) => {
  const { onSubmit, inputRef, onClearInputValue, value, fullWidth, ...rest } = props
  const [, setSearchHistory] = useStorage<string []>('cartAiSearchRecommend', [], STORAGE_EVENT.localStorage)

  const innerRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(inputRef, () => innerRef.current as HTMLInputElement)

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    if (innerRef.current) {
      const inputValue = innerRef.current?.value
      setSearchHistory(v => [...new Set([...v, inputValue])])
      onSubmit?.(inputValue)
    }
  }

  const handleClearInputValue = () => {
    if (innerRef.current) {
      innerRef.current.focus()
    }
    onClearInputValue?.()
  }

  return (
    <form onSubmit={handleSubmit} {...stylex.props(fullWidth && styles.fullWidth)}>
      <Input
        inputRef={innerRef}
        xStyles={{ inputBase: { root: styles.input } }}
        endAdornment={
          value
            ? (
              <IconButton variant="text" color="grey" onClick={handleClearInputValue}>
                <ErrorOutlined />
              </IconButton>
            )
            : <Search color={colors.grey600} />
        }
        value={value}
        fullWidth={fullWidth}
        {...rest}
      />
    </form>
  )
}
